// First-party cookie helpers. Used in place of localStorage because:
//   1. localStorage is blocked in our runtime.
//   2. Cookies are sync-readable pre-paint (no FOUC), survive iframe boundary
//      when same parent domain, and pass through SSR if we ever add it.

export interface CookieOptions {
  maxAgeDays?: number;
  path?: string;
  sameSite?: "Strict" | "Lax" | "None";
  secure?: boolean;
}

export function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${encodeURIComponent(name)}=`;
  const parts = document.cookie ? document.cookie.split("; ") : [];
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      try {
        return decodeURIComponent(part.slice(prefix.length));
      } catch {
        return null;
      }
    }
  }
  return null;
}

export function writeCookie(name: string, value: string, opts: CookieOptions = {}): void {
  if (typeof document === "undefined") return;
  const maxAge = (opts.maxAgeDays ?? 365) * 24 * 60 * 60;
  const path = opts.path ?? "/";
  const sameSite = opts.sameSite ?? "Lax";
  const parts = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    `Max-Age=${maxAge}`,
    `Path=${path}`,
    `SameSite=${sameSite}`,
  ];
  if (opts.secure) parts.push("Secure");
  try {
    document.cookie = parts.join("; ");
  } catch {
    // Cookie writes can throw in some sandboxed iframes — fail silently;
    // the in-memory React state remains authoritative for the session.
  }
}

export function deleteCookie(name: string, path = "/"): void {
  writeCookie(name, "", { maxAgeDays: 0, path });
}
